import { Checkbox } from 'antd';
import { useCallback, useMemo } from 'react';
import { useField } from '../field/useField.tsx';
import { useFormSetValue } from '../hooks/useFormSetValue.ts';
import { useTriggerComputedSync } from '../hooks/useTriggerComputedSync.ts';
import { useFormItemEnabled } from '../item/useFormItemEnabled.ts';

interface ICheckboxAllOptionsOption {
  id: string;
  disabled?: boolean;
}

interface ICheckboxAllOptionsInputProps {
  /** Nome del gruppo di checkbox. */
  groupName: string;
  /** Nome del campo oggetto delle opzioni (es. `campo_options@internal`). */
  optionsObjectName: string;
  /** Lista delle opzioni disponibili. */
  options: ICheckboxAllOptionsOption[];
  /** Stato di selezione corrente delle opzioni. */
  selectedOptions?: Record<string, boolean> | null;
  readonly?: boolean;
}

/**
 * Checkbox "Seleziona Tutti / Deseleziona Tutti" custom per la gestione delle
 * regole di visibilità e disabilitazione delle opzioni.
 *
 * Comportamento:
 * - Tutte deselezionate → checkbox vuota → click seleziona tutte
 * - Alcune selezionate → checkbox indeterminate → click deseleziona tutte
 * - Tutte selezionate → checkbox checked → click deseleziona tutte
 */
export function CheckboxAllOptionsInput(props: ICheckboxAllOptionsInputProps) {
  const { groupName, optionsObjectName, options, selectedOptions, readonly } =
    props;

  const group = useField(groupName);

  const fieldSetValue = useFormSetValue();
  const triggerSync = useTriggerComputedSync();
  const { visuallyDisabled } = useFormItemEnabled();

  const enabledOptions = useMemo(
    () => options.filter(option => !option.disabled),
    [options],
  );

  const { checkedCount, enabledCount } = useMemo(() => {
    const enabledCount = enabledOptions.length;
    const checkedCount = enabledOptions.filter(
      option => selectedOptions?.[option.id] === true,
    ).length;
    return { checkedCount, enabledCount };
  }, [enabledOptions, selectedOptions]);

  const allSelected = enabledCount > 0 && checkedCount === enabledCount;
  const someSelected = checkedCount > 0 && !allSelected;

  const handleChange = useCallback(() => {
    // Se c'è almeno un'opzione selezionata → deseleziona tutte
    // Se nessuna → seleziona tutte
    const shouldSelectAll = checkedCount === 0;

    // Chiamo setValue su ogni singolo campo (campo@object.id)
    for (const option of enabledOptions) {
      fieldSetValue(`${optionsObjectName}.${option.id}`, shouldSelectAll, {
        shouldDirty: true,
        shouldTouch: true,
        shouldSyncComputedValues: false,
      });
    }

    triggerSync();
  }, [
    checkedCount,
    enabledOptions,
    optionsObjectName,
    fieldSetValue,
    triggerSync,
  ]);

  if (enabledCount === 0) return null;

  return (
    <Checkbox
      checked={allSelected}
      indeterminate={someSelected}
      onChange={readonly ? undefined : handleChange}
      disabled={visuallyDisabled || group.enabled === false}
    />
  );
}
